import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import {
  AttackExecutionMetadataSchema,
  type AiDiscoveryExecutionMetadataDto,
} from '@terra/shared-contracts-agent-execution';
import type { AiDiscoverySummarySchema } from '@terra/shared-contracts-ai-applications';
import type { z } from 'zod';

export type AiDiscoverySummary = z.infer<typeof AiDiscoverySummarySchema>;
export type DiscoveryStatus = AiDiscoverySummary['discoveryStatus'];

export const DISCOVERY_EXECUTION_STATUS_COLOR_MAP: Record<
  AgentExecutionDto['status'],
  string
> = {
  PENDING: 'blue',
  RUNNING: 'blue',
  COMPLETED: 'green',
  FAILED: 'red',
  BLOCKED: 'red',
  ABORTED: 'gray',
};

export function getAiDiscoveryMetadata(
  metadata: AgentExecutionDto['metadata'],
): AiDiscoveryExecutionMetadataDto | null {
  const parsed = AttackExecutionMetadataSchema.safeParse(metadata);

  return parsed.success ? (parsed.data.aiDiscovery ?? null) : null;
}

export function resolveDiscoveryMongoTraceId({
  metadata,
  traces,
}: {
  metadata?: AgentExecutionDto['metadata'];
  traces?: ReadonlyArray<{ id: string }>;
}): string | undefined {
  const parsed = AttackExecutionMetadataSchema.safeParse(metadata);
  const fromMetadata = parsed.success
    ? (parsed.data.aiDiscovery?.traceId ?? parsed.data.attackTraceId)
    : undefined;

  return fromMetadata ?? traces?.at(-1)?.id;
}

export function isDiscoveryAccessDeniedFailure(
  reason: string | null | undefined,
): boolean {
  if (!reason?.trim()) {
    return false;
  }

  const lower = reason.toLowerCase();

  return (
    lower.includes('access_denied') ||
    lower.includes('access denied') ||
    lower.includes("don't have access") ||
    lower.includes('do not have access') ||
    lower.includes('not entitled') ||
    lower.includes('entitlement') ||
    lower.includes('permission denied') ||
    lower.includes('required scopes') ||
    lower.includes('check your permissions')
  );
}

export function getDiscoveryFailureReason({
  metadata,
  runStatus,
  statusDescription,
}: {
  metadata: AiDiscoveryExecutionMetadataDto | null;
  runStatus: AgentExecutionDto['status'];
  statusDescription?: string | null;
}): string | null {
  if (runStatus === 'ABORTED') {
    return statusDescription?.trim() || metadata?.failureReason?.trim() || null;
  }

  const reason = metadata?.failureReason?.trim();

  if (!reason || metadata?.verdictStatus === 'VERIFIED') {
    return null;
  }

  if (
    metadata?.verdictStatus === 'FAILED' ||
    runStatus === 'FAILED' ||
    runStatus === 'BLOCKED'
  ) {
    return reason;
  }

  return null;
}

const MCP_TOOL_ERROR_PREFIX =
  /^(?:ToolException:\s*)?MCP tool '[^']+'(?: on server '[^']+')? returned an error:\s*/i;
const EMBEDDED_JSON_SUFFIX = /(\{[\s\S]*\})\s*$/;
const ECONNREFUSED_HOST = /connect\s+ECONNREFUSED\s+(\S+)/i;
const ENOTFOUND_HOST = /(?:getaddrinfo\s+)?ENOTFOUND\s+(\S+)/i;
const ETIMEDOUT_HOST = /(?:connect\s+)?ETIMEDOUT\s+(\S+)/i;

const extractEmbeddedFailureMessage = (detail: string): string => {
  const jsonMatch = detail.match(EMBEDDED_JSON_SUFFIX);

  if (!jsonMatch || jsonMatch.index === undefined) {
    return detail;
  }

  try {
    const parsed: unknown = JSON.parse(jsonMatch[1]);

    if (!parsed || typeof parsed !== 'object') {
      return detail;
    }

    const record = parsed as Record<string, unknown>;
    const message =
      typeof record.message === 'string' ? record.message.trim() : '';
    const errorLabel =
      typeof record.error === 'string' ? record.error.trim() : '';
    const extracted = message || errorLabel;

    if (!extracted) {
      return detail;
    }

    const prefix = detail.slice(0, jsonMatch.index).replace(/:\s*$/, '').trim();

    if (
      !prefix ||
      prefix.toLowerCase() === 'proxy error' ||
      /^streamable http error(?:\s*:\s*error posting to endpoint)?$/i.test(
        prefix,
      )
    ) {
      return extracted;
    }

    return `${prefix}: ${extracted}`;
  } catch {
    return detail;
  }
};

export type DiscoveryProbeFailureTranslate = (
  key: 'failureConnectionRefused' | 'failureHostNotFound' | 'failureTimedOut',
  values: { host: string },
) => string;

const humanizeConnectivityFailureDetail = ({
  detail,
  t,
}: {
  detail: string;
  t: DiscoveryProbeFailureTranslate;
}): string => {
  const refused = detail.match(ECONNREFUSED_HOST);

  if (refused?.[1]) {
    return t('failureConnectionRefused', { host: refused[1] });
  }

  const notFound = detail.match(ENOTFOUND_HOST);

  if (notFound?.[1]) {
    return t('failureHostNotFound', { host: notFound[1] });
  }

  const timedOut = detail.match(ETIMEDOUT_HOST);

  if (timedOut?.[1]) {
    return t('failureTimedOut', { host: timedOut[1] });
  }

  return detail;
};

export function formatDiscoveryProbeFailureDetail(
  reason: string | null | undefined,
  t: DiscoveryProbeFailureTranslate,
): string | null {
  const trimmed = reason?.trim();

  if (!trimmed) {
    return null;
  }

  const withoutPrefix = trimmed.replace(MCP_TOOL_ERROR_PREFIX, '').trim();
  const base = withoutPrefix.length > 0 ? withoutPrefix : trimmed;

  return humanizeConnectivityFailureDetail({
    detail: extractEmbeddedFailureMessage(base),
    t,
  });
}

export function resolveDiscoveryProbeDisplayStatus({
  execution,
}: {
  execution: AgentExecutionDto;
}): AgentExecutionDto['status'] | 'ACCESS_DENIED' {
  const metadata = getAiDiscoveryMetadata(execution.metadata);
  const failureReason = getDiscoveryFailureReason({
    metadata,
    runStatus: execution.status,
  });

  if (
    execution.status === 'FAILED' &&
    isDiscoveryAccessDeniedFailure(failureReason)
  ) {
    return 'ACCESS_DENIED';
  }

  return execution.status;
}

export function shouldShowDiscoveryProtocolColumn({
  executions,
}: {
  executions: AgentExecutionDto[];
}): boolean {
  const protocols = new Set(
    executions.flatMap((execution) => {
      const protocol = getAiDiscoveryMetadata(
        execution.metadata,
      )?.targetProtocol;

      return protocol ? [protocol] : [];
    }),
  );

  return protocols.size > 1;
}

export function getExecutionLabel({
  execution,
}: {
  execution: AgentExecutionDto;
}): string {
  const aiDiscoveryMetadata = getAiDiscoveryMetadata(execution.metadata);

  if (aiDiscoveryMetadata?.mcpToolName) {
    return aiDiscoveryMetadata.mcpToolName;
  }

  if (aiDiscoveryMetadata?.componentType === 'MCP_SERVER') {
    return (
      aiDiscoveryMetadata.url ??
      execution.objective ??
      execution.craftName ??
      execution.id
    );
  }

  return (
    execution.objective ??
    execution.craftDescription ??
    execution.craftName ??
    aiDiscoveryMetadata?.url ??
    execution.id
  );
}

const DISCOVER_CANDIDATE_OBJECTIVE =
  /^Discover\s+(\S+)\s+candidate(?:\s+(.+))?$/i;

const hostFromUrl = (value: string): string | null => {
  try {
    return new URL(value).host;
  } catch {
    return null;
  }
};

export function getAgentDiscoveryHistoryTitle({
  execution,
}: {
  execution: AgentExecutionDto;
}): string {
  const metadata = getAiDiscoveryMetadata(execution.metadata);
  const objective = execution.objective?.trim() ?? '';
  const candidateMatch = objective.match(DISCOVER_CANDIDATE_OBJECTIVE);

  if (candidateMatch) {
    const kind = candidateMatch[1];
    const rest = candidateMatch[2]?.trim();
    const host = rest ? hostFromUrl(rest) : null;

    if (host) {
      return `${kind} · ${host}`;
    }

    return `${kind} candidate`;
  }

  if (metadata?.targetProtocol && metadata.url) {
    const host = hostFromUrl(metadata.url);

    if (host) {
      return `${metadata.targetProtocol} · ${host}`;
    }
  }

  return getExecutionLabel({ execution });
}

export function toDiscoveryTraceViewStatus(
  status: AgentExecutionDto['status'],
): 'running' | 'completed' | 'error' | 'aborted' {
  switch (status) {
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
    case 'BLOCKED':
      return 'error';
    case 'ABORTED':
      return 'aborted';
    default:
      return 'running';
  }
}

export function getAgentDiscoveryHistoryDetailUrl({
  execution,
}: {
  execution: AgentExecutionDto;
}): string | null {
  const metadata = getAiDiscoveryMetadata(execution.metadata);
  const fromMetadata = metadata?.url?.trim();

  if (fromMetadata) {
    return fromMetadata;
  }

  const objective = execution.objective?.trim() ?? '';
  const candidateMatch = objective.match(DISCOVER_CANDIDATE_OBJECTIVE);
  const rest = candidateMatch?.[2]?.trim();

  if (rest?.startsWith('http')) {
    return rest;
  }

  return null;
}

export function formatDiscoveryDuration({
  startedAt,
  completedAt,
  durationMs,
}: {
  startedAt: Date | string | null | undefined;
  completedAt: Date | string | null | undefined;
  durationMs?: number | null;
}): string {
  if (durationMs != null && durationMs >= 0) {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  }

  if (!startedAt || !completedAt) {
    return '—';
  }

  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
}

export function hasActiveDiscoveryExecution(
  executions: AgentExecutionDto[],
): boolean {
  return executions.some(
    (execution) =>
      execution.status === 'RUNNING' || execution.status === 'PENDING',
  );
}

export function canRetryDiscoveryProbe({
  execution,
}: {
  execution: AgentExecutionDto;
}): boolean {
  const metadata = getAiDiscoveryMetadata(execution.metadata);

  if (!metadata?.candidateId) {
    return false;
  }

  if (execution.status === 'PENDING' || execution.status === 'RUNNING') {
    return false;
  }

  return metadata.verdictStatus === 'FAILED';
}

export function resolveDiscoveryStatusForDisplay({
  discoveryStatus,
  hasActiveExecution,
  pendingProbeCount,
  totalProbeCount,
  verifiedProbeCount,
  awaitingCodeDiscovery,
}: {
  discoveryStatus: DiscoveryStatus;
  hasActiveExecution: boolean;
  pendingProbeCount: number;
  totalProbeCount?: number;
  verifiedProbeCount?: number;
  awaitingCodeDiscovery?: boolean;
}): DiscoveryStatus {
  if (pendingProbeCount > 0) {
    return 'RUNNING';
  }

  if (
    discoveryStatus === 'RUNNING' &&
    totalProbeCount &&
    !verifiedProbeCount &&
    !awaitingCodeDiscovery
  ) {
    return 'FAILED';
  }

  if (
    hasActiveExecution &&
    (discoveryStatus === 'PENDING' || discoveryStatus === 'RUNNING')
  ) {
    return 'RUNNING';
  }

  return discoveryStatus;
}
